import { useEffect, useMemo } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { CalendarGap } from '@atom/components/workOrders/workOrdersCalendar/weekViewUtils';
import { usePreferences } from '@atom/hooks/usePreferences';
import { useSchemas } from '@atom/hooks/useSchemas';
import {
  DatePicker,
  DateTimePicker,
  Icon,
  List,
  Switch,
  TextField,
} from '@atom/mui';
import { InventoryAssetDetailType, SearchAsset } from '@atom/types/inventory';
import { WorkOrderType } from '@atom/types/work';
import {
  SearchWorkTemplate,
  WorkTemplateListItem,
} from '@atom/types/workTemplate';
import { getUpdatedOtherDate } from '@atom/utilities/timeUtilities';
import { getWorkTypeIconSvg } from '@atom/utilities/workTypeIconUtilities';

import InventoryAssetTile from '../inventoryAssetTree/InventoryAssetTile';

import { getStartAndDueDateInitialValues } from './createWorkUtilities';

import classes from './createWorkModals.module.css';

const { ListItem, ListItemText } = List;

interface Props {
  name: string;
  onNameChange: (name: string) => void;
  dueDate?: Date;
  onDueDateChange: (date: Date) => void;
  plannedStartDate: Date;
  setPlannedStartDate: (date: Date) => void;
  asset: InventoryAssetDetailType | SearchAsset;
  onAssetChange: (asset: InventoryAssetDetailType) => void;
  onAssetViewClick: () => void;
  isLocationBased: boolean;
  onLocationBasedChange: (value: boolean) => void;
  workTemplate?: WorkTemplateListItem | SearchWorkTemplate;
  disableAssetChange?: boolean;
  requireAsset?: boolean;
  fromCalendarDate?: Date;
  fromCalendarGap?: CalendarGap;
}

const styles = {
  icon: {
    marginRight: '2rem',
  },
};

const CreateSingleWork = ({
  name,
  onNameChange,
  dueDate,
  onDueDateChange,
  plannedStartDate,
  setPlannedStartDate,
  asset,
  onAssetChange,
  onAssetViewClick,
  isLocationBased,
  onLocationBasedChange,
  workTemplate,
  disableAssetChange,
  requireAsset,
  fromCalendarDate,
  fromCalendarGap,
}: Props) => {
  const { schemas = [] } = useSchemas();
  const preferences = usePreferences();
  const isWorkNamingDisabled = preferences.workOrders?.workNamingDisabled;

  const workTemplateSchema = useMemo(() => {
    return schemas.find(({ id }) => id === workTemplate?.schemaId);
  }, [workTemplate]);

  useEffect(() => {
    // Only initialize range if both are set to initial value
    const formContainsDefaultRange =
      dueDate?.valueOf() === plannedStartDate?.valueOf();
    if (formContainsDefaultRange) {
      if (workTemplate?.appointmentsEnabled) {
        const values = getStartAndDueDateInitialValues({
          fromCalendarDate,
          fromCalendarGap,
        });

        setPlannedStartDate(values.plannedStartDate);
        onDueDateChange(values.dueDate);
      } else if (fromCalendarDate || !!fromCalendarGap) {
        // for non-appointment templates
        // - set the dueDate if created from a calendar day or gap
        onDueDateChange(fromCalendarDate || new Date(fromCalendarGap.dueDate));
      }
    }
  }, [workTemplate]);

  const handleDueDateTimeChange = (date?: Date) => {
    if (!date || date.toString() === 'Invalid Date') {
      // Do not allow user to clear date once set
      onDueDateChange(dueDate);
    } else {
      // Ensures date times are on the same day
      const updatedPlannedStartDate = getUpdatedOtherDate(
        date,
        plannedStartDate,
      );

      onDueDateChange(date);
      setPlannedStartDate(updatedPlannedStartDate);
    }
  };

  const handlePlannedStartDateTimeChange = (date?: Date) => {
    if (!date || date.toString() === 'Invalid Date') {
      // Do not allow user to clear date once set
      setPlannedStartDate(plannedStartDate);
    } else {
      // Ensures date times are on the same day
      const updatedDueDate = getUpdatedOtherDate(date, dueDate);

      setPlannedStartDate(date);
      onDueDateChange(updatedDueDate);
    }
  };

  const handleDueDateChange = (date?: Date) => {
    if (!date) {
      onDueDateChange(null);
    } else {
      onDueDateChange(date);
    }
  };

  const workTemplateType = R.pathOr(
    WorkOrderType.DEFAULT,
    ['type'],
    workTemplate,
  );
  const showAssetAndLocation = workTemplateType === WorkOrderType.DEFAULT;
  const schemaSubtext =
    workTemplateType === WorkOrderType.DEFAULT
      ? workTemplateSchema?.name || 'All Asset Types'
      : '';

  const errorDateBoundary = useMemo(() => {
    if (workTemplate?.appointmentsEnabled) {
      return plannedStartDate?.valueOf() > dueDate?.valueOf();
    }

    return false;
  }, [dueDate, plannedStartDate, workTemplate]);

  return (
    <>
      {workTemplate && (
        <div className={clsx(classes.modalRow, classes.workTemplate)}>
          <Icon style={styles.icon}>
            <img
              src={getWorkTypeIconSvg(
                workTemplate?.isParent,
                workTemplate?.parentId,
              )}
            />
          </Icon>
          <ListItem disableGutters style={{ margin: 0, padding: 0 }}>
            <ListItemText
              style={{ margin: 0 }}
              primary={workTemplate.name}
              secondary={schemaSubtext}
            />
          </ListItem>
        </div>
      )}
      <div className={clsx(classes.modalRow, classes.name)}>
        <Icon style={styles.icon}>
          <img src={renameIcon} />
        </Icon>
        <TextField
          required
          id="workOrderName"
          label="Work Name"
          value={name}
          onChange={event => onNameChange(event.target.value)}
          disabled={!!isWorkNamingDisabled}
        />
      </div>
      {workTemplate?.appointmentsEnabled ? (
        <>
          <div className={clsx(classes.modalRow, classes.dueDate)}>
            <Icon style={styles.icon}>event</Icon>
            <DateTimePicker
              key="plannedStartDate"
              label="Planned Start Date *"
              value={plannedStartDate}
              onChange={handlePlannedStartDateTimeChange}
              hideClear
            />
          </div>
          <div className={clsx(classes.modalRow, classes.dueDate)}>
            <div className={classes.iconSpacer} />
            <DateTimePicker
              key="dueDate"
              label="Due Date *"
              value={dueDate}
              onChange={handleDueDateTimeChange}
              helperText={
                errorDateBoundary &&
                'Due date cannot be before planned start date.'
              }
              error={errorDateBoundary}
              hideClear
            />
          </div>
        </>
      ) : (
        <div className={clsx(classes.modalRow, classes.dueDate)}>
          <Icon style={styles.icon}>event</Icon>
          <DatePicker
            label="Due Date"
            value={dueDate}
            onChange={handleDueDateChange}
          />
        </div>
      )}
      {showAssetAndLocation && (
        <>
          <div className={classes.modalRow}>
            <Icon style={styles.icon}>category</Icon>
            <InventoryAssetTile
              asset={asset}
              onClick={onAssetViewClick}
              onRemove={!disableAssetChange ? () => onAssetChange(null) : null}
              requireAsset={requireAsset}
            />
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <Switch
              checked={isLocationBased}
              onChange={() => onLocationBasedChange(!isLocationBased)}
              name="isLocationBased"
              label="Location"
              disabled={!!asset}
            />
          </div>
        </>
      )}
    </>
  );
};

export default CreateSingleWork;
